import React from 'react';
import {
  GenericCardProps,
  DownloadCardProps,
  ProductCardProps,
  Result,
  GenericCardComponentProps,
  ProductCardComponentProps,
} from './../../../types/search';
import useIsMobile from './../../hooks/is-mobile';
import SvgIcon from './../../utils/SvgIcon';
import SearchNoResults from './SearchNoResults';
import ResponsiveImage from './../../utils/ResponsiveImage';
import { useState, useEffect } from 'react';
import { useSearchConfig } from '../hooks/useSearchConfig';

const fireTrackingEvent = (data: ProductCardProps | GenericCardProps, trackingEndpoint?: string) => {
  if (data.searchTracking && trackingEndpoint) {
    fetch(trackingEndpoint, {
      method: 'POST',
      body: JSON.stringify(data.searchTracking),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
};

const GenericCard = ({ data, isMobile, config }: GenericCardComponentProps) => (
  <a
    href={data.relativePath}
    className="search__generic-card"
    onClick={() => fireTrackingEvent(data, config?.trackingEndpoint)}
  >
    <div>
      <div className="search__generic-card-info">
        {data.publishingDate && <span className="search__generic-card-date">{data.publishingDate}</span>}
      </div>
      <div className="search__generic-card-title-wrapper">
        {data.imageInfo && isMobile && (
          <div className="search__generic-card-image-wrapper">
            <ResponsiveImage
              image={{
                altText: data.imageInfo.altText || data.heading,
                src: data.imageInfo.src,
                sizes: data.imageInfo.sizes,
                srcSets: data.imageInfo.srcSets,
              }}
              width={300}
              height={200}
              className="search__generic-card-image"
            />
          </div>
        )}
        <h3 className="search__generic-card-title">{data.heading}</h3>
      </div>
      {data.description && <p className="search__generic-card-description">{data.description}</p>}
    </div>
    {data.imageInfo && !isMobile && (
      <div className="search__generic-card-image-wrapper">
        <ResponsiveImage
          image={{
            altText: data.imageInfo.altText || data.heading,
            src: data.imageInfo.src,
            sizes: data.imageInfo.sizes,
            srcSets: data.imageInfo.srcSets,
          }}
          width={300}
          height={200}
          className="search__generic-card-image"
        />
      </div>
    )}
  </a>
);

const DownloadCard = ({
  data,
  downloadLabel,
  lastUpdatedLabel,
}: {
  data: DownloadCardProps;
  downloadLabel?: string;
  lastUpdatedLabel?: string;
}) => (
  <a href={data.relativePath} download className="search__download-card">
    <div className="search__download-card-left">
      <div className="search__download-card-left-top">
        {data.docTag && <span className="search__download-card-tag">{data.docTag}</span>}
        <div className="search__download-card-file-info">
          {data.docType && <p className="search__download-card-filetype">{data.docType}</p>}
          {data.docSize && <p className="search__download-card-filesize">{data.docSize}</p>}
        </div>
      </div>
      <div className="search__download-card-left-bottom">
        <h3 className="search__download-card-title">{data.heading}</h3>
        <p className="search__download-card-last-updated">
          {lastUpdatedLabel ?? 'Last updated:'}: {data.startPublish}
        </p>
      </div>
    </div>
    <div className="search__download-card-right">
      <div className="search__download-card-right-icon-wrapper">
        <p className="search__download-card-right-text">{downloadLabel ?? 'Download'}</p>
        <SvgIcon
          additionalClasses="search__download-card-icon"
          iconName="download"
          className="icon"
          viewBox="0 0 24 24"
        />
      </div>
    </div>
  </a>
);

const ProductCard = ({ data, isMobile, config }: ProductCardComponentProps) => {
  const typeModifier = data.typeKey ? data.typeKey.split('.').pop()?.toLowerCase() : '';
  return (
    <a
      href={data.relativePath}
      className={`search__product-card ${typeModifier ? 'search__product-card--' + typeModifier : ''}`}
      onClick={() => fireTrackingEvent(data, config?.trackingEndpoint)}
    >
      <div className="search__product-card-inner">
        <div className="search__product-card-info">
          {data.parentCategory && <span className="search__product-card-parent-category">{data.parentCategory}</span>}
          {data.type && <span className="search__product-card-type">{data.type}</span>}
          {data.numberOfVariantsLabel && !isMobile && (
            <span className="search__product-card-variant-count">{data.numberOfVariantsLabel}</span>
          )}
        </div>
        <div className="search__product-card-title-wrapper">
          {data.imageInfo && isMobile && (
            <div className="search__product-card-image-wrapper">
              <ResponsiveImage
                image={{
                  altText: data.imageInfo.altText ?? data.heading ?? '',
                  src: data.imageInfo.src,
                  sizes: data.imageInfo.sizes,
                  srcSets: data.imageInfo.srcSets,
                }}
                width={300}
                height={200}
                className="search__product-card-image"
              />
            </div>
          )}
          <div className="search__product-card-title-inner-wrapper">
            <h3 className="search__product-card-title">{data.heading}</h3>
            {isMobile && data.numberOfVariantsLabel && (
              <span className="search__product-card-variant-count">{data.numberOfVariantsLabel}</span>
            )}
          </div>
        </div>
        {data.description && <p className="search__product-card-description">{data.description}</p>}
        {data.categories && (
          <div className="search__product-card-category-list">
            {data.categories.map(category => (
              <span key={category} className="search__product-card-category-list-category">
                {category}
              </span>
            ))}
          </div>
        )}
      </div>
      {data.imageInfo && !isMobile && (
        <div className="search__product-card-image-wrapper">
          <ResponsiveImage
            image={{
              altText: data.imageInfo.altText ?? data.heading ?? '',
              src: data.imageInfo.src,
              sizes: data.imageInfo.sizes,
              srcSets: data.imageInfo.srcSets,
            }}
            width={300}
            height={200}
            className="search__product-card-image"
          />
        </div>
      )}
    </a>
  );
};

interface LocalSearchResultsContainerProps {
  results: Result[];
  searchTerm: string;
  lastSearchedTerm?: string;
  isLoading: boolean;
  hasSearched?: boolean;
  noResultsText?: string;
  activeTabKey?: string;
  downloadLabel?: string;
  lastUpdatedLabel?: string;
  featuredArticle?: any;
}

const SearchResultsContainer = ({
  results,
  searchTerm,
  lastSearchedTerm = '',
  isLoading,
  hasSearched = false,
  noResultsText,
  activeTabKey,
  downloadLabel,
  lastUpdatedLabel,
  featuredArticle,
}: LocalSearchResultsContainerProps) => {
  const isMobile = useIsMobile(1023);
  const [internalHasSearched, setInternalHasSearched] = useState(false);
  const [internalLastSearchedTerm, setInternalLastSearchedTerm] = useState('');
  const config = useSearchConfig();

  useEffect(() => {
    if (isLoading) {
      setInternalHasSearched(true);
      setInternalLastSearchedTerm(searchTerm);
    }
  }, [isLoading, searchTerm]);

  useEffect(() => {
    if (hasSearched && lastSearchedTerm) {
      setInternalHasSearched(true);
      setInternalLastSearchedTerm(lastSearchedTerm);
    }
  }, [hasSearched, lastSearchedTerm]);

  const isProductTab = activeTabKey?.toLowerCase().includes('product');
  const isDocumentTab = activeTabKey?.toLowerCase().includes('document');

  const renderCard = (result: Result) => {
    if (isProductTab) {
      return (
        <ProductCard
          key={`${activeTabKey}-${result.id}`}
          data={result as ProductCardProps}
          isMobile={isMobile}
          config={{ trackingEndpoint: config.siteSearchApi + 'tracking' }}
        />
      );
    }

    if (isDocumentTab) {
      return (
        <DownloadCard
          key={`${activeTabKey}-${result.id}-${JSON.stringify(result)}`}
          data={result as DownloadCardProps}
          downloadLabel={downloadLabel}
          lastUpdatedLabel={lastUpdatedLabel}
        />
      );
    }

    if (result.type === 'download' || (result as DownloadCardProps).docTag || (result as DownloadCardProps).docType) {
      return (
        <DownloadCard
          key={`${activeTabKey}-${result.id}-${JSON.stringify(result)}`}
          data={result as DownloadCardProps}
          downloadLabel={downloadLabel}
          lastUpdatedLabel={lastUpdatedLabel}
        />
      );
    }

    if (result.type === 'product') {
      return (
        <ProductCard
          key={`${activeTabKey}-${result.id}-${JSON.stringify(result)}`}
          data={result as ProductCardProps}
          isMobile={isMobile}
          config={{ trackingEndpoint: config.siteSearchApi + 'tracking' }}
        />
      );
    }

    return (
      <GenericCard
        key={`${activeTabKey}-${result.id}-${JSON.stringify(result)}`}
        data={result as GenericCardProps}
        isMobile={isMobile}
        config={{ trackingEndpoint: config.siteSearchApi + 'tracking' }}
      />
    );
  };

  let content;

  const allResults = featuredArticle ? [featuredArticle, ...results] : results;

  if (allResults.length > 0) {
    content = allResults.map(renderCard);
  } else if ((hasSearched || internalHasSearched) && !isLoading) {
    const termToShow = lastSearchedTerm || internalLastSearchedTerm;
    content = <SearchNoResults noResultsText={noResultsText?.replace('{query}', termToShow) ?? ''} />;
  } else {
    content = null;
  }

  return <>{content}</>;
};

export default SearchResultsContainer;
